import CRMTabs, { CRMTabItem } from '@components/Common/CRMTabs';
import { Chip } from '@mui/material';
import React, { createContext, useContext, useMemo, useState } from 'react';
import './index.scss';

/**
 * The one page header: icon, H1, count, one line of context, actions on the right, and the
 * page's tabs directly underneath it.
 *
 * The console had three. CRM lists drew icon + H1 + count. Dashboard, Notifications and Email
 * Campaigns opened on a tab bar with no title at all, then a hero card whose H2 was a sentence.
 * Companies and Applications put their tabs ABOVE the H1, while External Jobs put them below its
 * stat tiles. The order is now fixed: header, tabs, content.
 *
 * CRMPageTemplate renders this, so every list page gets it without asking. A page that is not a
 * list (a dashboard, a tabbed suite) renders it directly.
 */

export interface PageHeaderTabs {
  value: number;
  onChange: (index: number) => void;
  tabs: CRMTabItem[];
}

/**
 * Tabs a parent owns while the child draws the header.
 *
 * /applications switches between two pages that each render their own CRMPageTemplate. The
 * parent knows the tabs and the children know the header, so the tabs travel down through this
 * rather than through a prop on every page that might be put under a tab.
 */
export const PageTabsContext = createContext<PageHeaderTabs | null>(null);

/* -------------------------------------------------------------------------------------------- *
 * Header slots: a tab's PageHero, said in the page header instead of as a card.
 *
 * Each tab of a tabbed suite was written to open with its own PageHero (an eyebrow, an H2
 * sentence and that tab's actions). Under a real page header that is a second header. Rather
 * than rewrite six tabs, a page wraps each panel in <PageHeroSlot>: the active tab's PageHero
 * renders its sentence as the header's subtitle and its actions in the header's action area,
 * and the inactive ones render nothing. Outside a slot, PageHero is unchanged.
 * -------------------------------------------------------------------------------------------- */

export interface PageHeaderSlots {
  subtitleEl: HTMLElement | null;
  actionsEl: HTMLElement | null;
  setSubtitleEl: (el: HTMLElement | null) => void;
  setActionsEl: (el: HTMLElement | null) => void;
}

export const usePageHeaderSlots = (): PageHeaderSlots => {
  const [subtitleEl, setSubtitleEl] = useState<HTMLElement | null>(null);
  const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
  return useMemo(() => ({ subtitleEl, actionsEl, setSubtitleEl, setActionsEl }), [subtitleEl, actionsEl]);
};

export interface PageHeroSlotValue {
  subtitleEl: HTMLElement | null;
  actionsEl: HTMLElement | null;
  active: boolean;
}

export const PageHeroSlotContext = createContext<PageHeroSlotValue | null>(null);

export const PageHeroSlot: React.FC<{ slots: PageHeaderSlots; active: boolean; children: React.ReactNode }> = ({
  slots,
  active,
  children,
}) => {
  const value = useMemo(
    () => ({ subtitleEl: slots.subtitleEl, actionsEl: slots.actionsEl, active }),
    [slots.subtitleEl, slots.actionsEl, active],
  );
  return <PageHeroSlotContext.Provider value={value}>{children}</PageHeroSlotContext.Provider>;
};

interface PageHeaderProps {
  title: string;
  icon: React.ReactNode;
  /** Record count, shown as the mono pill beside the title. */
  count?: number;
  /** One line on what the page is for. Wraps rather than truncating on a phone. */
  subtitle?: React.ReactNode;
  /** Right-aligned. Icon buttons first, the one primary action last. */
  actions?: React.ReactNode;
  /** Falls back to PageTabsContext when absent. */
  tabs?: PageHeaderTabs;
  /** Receives a tab's PageHero; see PageHeroSlot. Replaces `subtitle` when given. */
  slots?: PageHeaderSlots;
  /** A row inside the band, under the title (the phone filter bar). */
  below?: React.ReactNode;
  /** Beside the title, after the count: the page's access, e.g. `<ViewOnlyChip />`. */
  badge?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, icon, count, subtitle, actions, tabs, slots, below, badge }) => {
  const inherited = useContext(PageTabsContext);
  const pageTabs = tabs ?? inherited;
  const hasActions = !!actions || !!slots;

  return (
    <>
      <header className={`cc-page-header${pageTabs ? ' cc-page-header--tabbed' : ''}`}>
        <div className="cc-page-header__inner">
          <div className={`cc-page-header__row${below ? ' cc-page-header__row--tight' : ''}`}>
            <div className="cc-page-header__lead">
              <span className="cc-page-header__icon" aria-hidden="true">
                {icon}
              </span>
              <div className="cc-page-header__copy">
                <div className="cc-page-header__title-row">
                  <h1 className="cc-page-header__title">{title}</h1>
                  {count !== undefined && (
                    // A count, not a status: the mono family and the accent tint are its identity.
                    // Size, weight, radius and padding come from the theme like every other chip.
                    <Chip className="cc-page-header__count" label={count.toLocaleString('en-IN')} />
                  )}
                  {badge}
                </div>
                {slots ? (
                  <p className="cc-page-header__subtitle" ref={slots.setSubtitleEl} />
                ) : (
                  subtitle && <p className="cc-page-header__subtitle">{subtitle}</p>
                )}
              </div>
            </div>
            {hasActions && (
              <div className="cc-page-header__actions">
                {slots && <span className="cc-page-header__slot" ref={slots.setActionsEl} />}
                {actions}
              </div>
            )}
          </div>
          {below}
        </div>
      </header>
      {pageTabs && (
        <CRMTabs
          className="cc-page-header__tabs"
          value={pageTabs.value}
          onChange={pageTabs.onChange}
          tabs={pageTabs.tabs}
        />
      )}
    </>
  );
};

export default PageHeader;
